import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { JobsiteUpdateDTO } from '../../api/generated';
import { useUpdateJobsiteMutation } from '../../api/mutations/jobsite';
import { useJobsiteList } from '../../api/queries/jobsite';
import { useUserContext } from '../../UserContext';
import { DefaultErrorScreen, DefaultLoadingScreen } from '../ApiLoadingGuard';

export function ManageJobsites() {
  const { t } = useTranslation();
  const { data: user } = useUserContext();
  const { mutate: updateJobsite, isPending: isSubmitPending } = useUpdateJobsiteMutation();
  const { data, isPending, isStale, isError } = useJobsiteList({ organisationId: user?.organisation.id });
  const isLoading = isPending || isStale;

  const [selectedJobsiteId, setSelectedJobsiteId] = useState<string | undefined>();
  const [updateData, setUpdateData] = useState<JobsiteUpdateDTO>({ name: '' });

  const isDataValid = updateData.name.length >= 3;

  function handleSubmit() {
    updateJobsite({ organisationId: user!.organisation!.id, jobsiteId: selectedJobsiteId!, data: updateData });
  }

  function handleDataUpdate(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setUpdateData((prev) => ({ ...prev, [e.target.id]: e.target.value }));
  }

  function handleSelectedJobsiteChange(e: SelectChangeEvent<string>) {
    setSelectedJobsiteId(e.target.value);
  }

  if (isError) {
    return <DefaultErrorScreen />;
  }

  if (isLoading) {
    return <DefaultLoadingScreen />;
  }

  return (
    <Box display='flex'>
      <FormControl sx={{ flexGrow: 0.5, marginRight: 2 }}>
        <InputLabel id='select-jobsite-label'>{t('administration.locationManagement.selectLocation')}</InputLabel>
        <Select
          labelId='select-jobsite-label'
          label={t('administration.locationManagement.selectLocation')}
          MenuProps={{ disableScrollLock: true }}
          value={selectedJobsiteId || ''}
          onChange={handleSelectedJobsiteChange}
        >
          {data.data.map((j, idx) => (
            <MenuItem value={j.id} key={idx}>
              {` ${j.name} `}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        sx={{ flexGrow: 0.5, marginRight: 2 }}
        id='name'
        label={t('administration.locationManagement.newName')}
        value={updateData.name}
        onChange={handleDataUpdate}
      />

      <Button sx={{ flexGrow: 0.25 }} variant='contained' onClick={handleSubmit} disabled={!isDataValid}>
        {isSubmitPending ? (
          <CircularProgress color='inherit' />
        ) : (
          t('administration.locationManagement.changeNameButton')
        )}
      </Button>
    </Box>
  );
}
